/**
 * Adhesive formats a prepackaged (art. 9 Reg. UE 1169/2011) label is printed
 * on. Deliberately separate from `LABEL_FORMAT_CATALOG`, which serves the QR
 * thermal printer and contains sizes (24x24, 29x90, …) where the mandatory
 * particulars can never fit at a legible body size.
 *
 * The persisted key is the `(widthMm, heightMm)` pair, not the id: templates
 * created before this catalog keep working and simply read as "custom".
 */
export interface PrepackagedLabelFormat {
  id: string;
  widthMm: number;
  heightMm: number;
}

export const PREPACKAGED_LABEL_FORMAT_CATALOG: readonly PrepackagedLabelFormat[] = [
  { id: '100x150', widthMm: 100, heightMm: 150 },
  { id: '105x148', widthMm: 105, heightMm: 148 },
  { id: '100x100', widthMm: 100, heightMm: 100 },
  { id: '90x62', widthMm: 90, heightMm: 62 },
  { id: '80x60', widthMm: 80, heightMm: 60 },
  { id: '62x100', widthMm: 62, heightMm: 100 },
  { id: '58x100', widthMm: 58, heightMm: 100 },
] as const;

export const DEFAULT_PREPACKAGED_LABEL_FORMAT_ID = '100x150';

export function getPrepackagedLabelFormatById(id: string): PrepackagedLabelFormat | undefined {
  return PREPACKAGED_LABEL_FORMAT_CATALOG.find((format) => format.id === id);
}

/** `undefined` for a size outside the catalog — a legitimate custom format. */
export function findPrepackagedLabelFormat(
  widthMm: number,
  heightMm: number,
): PrepackagedLabelFormat | undefined {
  return PREPACKAGED_LABEL_FORMAT_CATALOG.find(
    (format) => format.widthMm === widthMm && format.heightMm === heightMm,
  );
}

export function getDefaultPrepackagedLabelFormat(): PrepackagedLabelFormat {
  const fallback = getPrepackagedLabelFormatById(DEFAULT_PREPACKAGED_LABEL_FORMAT_ID);
  if (!fallback) {
    throw new Error('default prepackaged label format missing from the catalog');
  }
  return fallback;
}
